import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { OMRADEN } from '../../modules/Instruktioner/utils/format';

/**
 * Modulväljaren i hjälpvyn: sökfält överst, grupper per område, "Utan
 * instruktion" sist. Öppnas under knappen (DS Del P), Escape stänger menyn
 * innan den stänger hjälpvyn.
 * @param {{ instruktioner: object[], beskrivningar: object[], aktuell: {typ: string, nyckel: string}|null,
 *          sidansVal: {typ: string, nyckel: string}|null, titel: string, onValj: (val: object) => void }} props
 */
export default function ModulValjare({ instruktioner, beskrivningar, aktuell, sidansVal, titel, onValj }) {
  const [oppen, setOppen] = useState(false);
  const [sok, setSok] = useState('');
  const rotRef = useRef(null);
  const sokRef = useRef(null);

  useEffect(() => {
    if (!oppen) return undefined;
    setSok('');
    setTimeout(() => sokRef.current && sokRef.current.focus(), 20);
    const klick = (e) => { if (rotRef.current && !rotRef.current.contains(e.target)) setOppen(false); };
    const tangent = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOppen(false); } };
    document.addEventListener('mousedown', klick);
    document.addEventListener('keydown', tangent, true);
    return () => { document.removeEventListener('mousedown', klick); document.removeEventListener('keydown', tangent, true); };
  }, [oppen]);

  const q = sok.trim().toLowerCase();
  const matchar = (t) => !q || String(t).toLowerCase().includes(q);
  const valt = (typ, nyckel) => aktuell && aktuell.typ === typ && aktuell.nyckel === nyckel;
  const valj = (val) => { setOppen(false); onValj(val); };
  const alt = (typ, nyckel, namn, tips) => (
    <button key={`${typ}-${nyckel}`} type="button" role="option" aria-selected={valt(typ, nyckel)}
      className={`hjalp-alt${valt(typ, nyckel) ? ' vald' : ''}`} onClick={() => valj({ typ, nyckel })}>
      <span>{namn}</span>
      {tips && !valt(typ, nyckel) && <span className="tips">{tips}</span>}
      {valt(typ, nyckel) && <Check size={14} strokeWidth={2.5} aria-hidden="true" />}
    </button>
  );

  const sidans = sidansVal && (sidansVal.typ === 'instruktion'
    ? instruktioner.find((i) => i.nyckel === sidansVal.nyckel)
    : beskrivningar.find((b) => b.key === sidansVal.nyckel));
  const sidansNamn = sidans ? (sidans.titel || sidans.title) : null;
  const grupper = OMRADEN.map((o) => [o, instruktioner.filter((i) => i.omrade === o && matchar(i.titel))])
    .filter(([, lista]) => lista.length);
  const utan = beskrivningar.filter((b) => matchar(b.title));
  const tomt = !grupper.length && !utan.length && !(sidansNamn && matchar(sidansNamn));

  return (
    <div className={`hjalp-valjare${oppen ? ' oppen' : ''}`} ref={rotRef}>
      <button type="button" className="hjalp-valjare-knapp" onClick={() => setOppen((v) => !v)} aria-haspopup="listbox" aria-expanded={oppen}>
        <span className="etikett">Modul</span>
        <span className="varde">{titel}</span>
        <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" className="pil" />
      </button>
      {oppen && (
        <div className="hjalp-valjare-meny">
          <div className="hjalp-valjare-sok">
            <Search size={14} strokeWidth={2} aria-hidden="true" />
            <input ref={sokRef} value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Sök modul" aria-label="Sök modul" />
          </div>
          <div className="hjalp-valjare-lista" role="listbox">
            {sidansNamn && matchar(sidansNamn) && (
              <>
                <div className="hjalp-grupp">Sidan du är på</div>
                {alt(sidansVal.typ, sidansVal.nyckel, sidansNamn, 'Aktuell')}
              </>
            )}
            {grupper.map(([o, lista]) => (
              <React.Fragment key={o}>
                <div className="hjalp-grupp">{o}</div>
                {lista.map((i) => alt('instruktion', i.nyckel, i.titel))}
              </React.Fragment>
            ))}
            {utan.length > 0 && (
              <>
                <div className="hjalp-grupp">Utan instruktion</div>
                {utan.map((b) => alt('beskrivning', b.key, b.title, 'Kort beskrivning'))}
              </>
            )}
            {tomt && <div className="hjalp-tom">Ingen modul matchar ”{sok}”</div>}
          </div>
        </div>
      )}
    </div>
  );
}
